import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';
import { Component, ReactNode } from 'react';
import * as Sentry from '@sentry/react';

import shrug from '../assets/images/shrug.png';
import { APP_VERSION } from '../constants/environment';
import { UserContext } from '../context';
import { requestHandler, sleep } from '../utils/generalUtils';
import { getToken } from '../utils/tokenManager';

interface ErrorBoundaryProps {
    children: ReactNode;
}

interface ErrorBoundaryState {
    error: string | null;
    newVersion: boolean;
}

interface ErrorReportData {
    user: unknown;
    errName: string;
    errMessage: string;
    location: string;
    error: string;
    version: string;
    type: string;
}

interface ApiResponse {
    success: boolean;
    payload?: string;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    static contextType = UserContext;
    declare context: React.ContextType<typeof UserContext>;

    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = {
            error: null,
            newVersion: false,
        };
    }

    async componentDidCatch(err: Error, info: React.ErrorInfo): Promise<void> {
        console.error('ErrorBoundary', err);

        try {
            // Sentry.Context is of type { [key: string]: unknown }, it deciding this object not
            // matching is just typescript being a picky bitch
            Sentry.captureException(err, {
                contexts: { react: info as unknown as Sentry.Context },
            });
            await Sentry.flush(2000);
        } catch (_sentryErr) {
            // no-op
        }

        const user = this.context;
        const error = err.toString();
        const chunkErr =
            error.indexOf('Loading chunk') !== -1 || error.indexOf('Loading CSS chunk') !== -1;
        const version = await this.getServerVersion();
        const differentVersion = version
            ? version?.replace('v', '') !== APP_VERSION?.replace('v', '')
            : false;
        if (!chunkErr && !differentVersion) {
            await this.sendError({
                user,
                errName: err.name,
                errMessage: err.message || 'Unknown error',
                location: window.location.href,
                error,
                version: APP_VERSION || 'unknown',
                type: chunkErr
                    ? 'New Version'
                    : differentVersion
                      ? 'Mismatch Versions (Oops alt)'
                      : 'Oops',
            });
        }
        if (chunkErr) this.setState({ newVersion: true });
        else this.setState({ error });
    }

    reset = async (): Promise<void> => {
        try {
            await Sentry.flush(1000);
        } catch (_e) {
            // no-op
        }
        window.location?.replace('/');
    };

    refresh = async (): Promise<void> => {
        try {
            await Sentry.flush(1000);
        } catch (_e) {
            // no-op
        }
        window.location.reload();
    };

    backup = async (): Promise<void> => {
        try {
            await Sentry.flush(1000);
        } catch (_e) {
            // no-op
        }
        window.history.back();
        await sleep(750);
        window.location.reload();
    };

    sendError = (data: ErrorReportData): Promise<unknown> => {
        const token = getToken();
        return requestHandler('POST', token ? '/api/errorReport' : '/api/public/errorReport', data);
    };

    getServerVersion = async (): Promise<string | false> => {
        const res: ApiResponse = await requestHandler('GET', '/api/public/version');
        if (res.success) return res.payload || false;
        return false;
    };

    render(): ReactNode {
        const { error, newVersion } = this.state;
        const { children } = this.props;
        if (error) {
            return (
                <Dialog open>
                    <DialogTitle>
                        <img
                            src={shrug}
                            alt='man-shrugging'
                            style={{
                                height: '30px',
                                width: '30px',
                                verticalAlign: 'sub',
                                marginRight: '10px',
                            }}
                        />
                        {"It's not you, it's us..."}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            {
                                "It's hard to say this, but something went wrong. Click go back or reset."
                            }
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.reset}>Reset</Button>
                        <Button onClick={this.backup}>Go Back</Button>
                    </DialogActions>
                </Dialog>
            );
        }
        if (newVersion) {
            return (
                <Dialog open>
                    <DialogTitle>
                        <span role='img' aria-label='celebrate'>
                            🎉
                        </span>
                        Yay, Time to Upgrade!
                        <span role='img' aria-label='celebrate'>
                            🎉
                        </span>
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            {
                                "We're busy working to improve your experience. We need to refresh to get you Current!"
                            }
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.refresh}>Refresh</Button>
                    </DialogActions>
                </Dialog>
            );
        }
        return children;
    }
}

export default ErrorBoundary;
